import { useCallback, useEffect, useState } from "react";

/**
 * Client-side bookmarks (localStorage). Keeps two separate collections:
 *   - "terms"    : glossary entries
 *   - "products" : catalogue items
 * Each entry stores { slug, name, category_slug, added_at }.
 * A `storage` event listener keeps every open tab in sync.
 */
const KEY = (bucket) => `bk_bookmarks_${bucket}`;
const CHANGE_EVENT = "bk-bookmarks-change";

function readAll(bucket) {
  try {
    const raw = localStorage.getItem(KEY(bucket));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function writeAll(bucket, items) {
  try {
    localStorage.setItem(KEY(bucket), JSON.stringify(items));
  } catch { /* silent (quota / private-mode) */ }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { bucket } }));
}

export function useBookmarks(bucket) {
  const [items, setItems] = useState(() => readAll(bucket));

  useEffect(() => {
    const refresh = (e) => {
      if (!e.detail || e.detail.bucket === bucket) setItems(readAll(bucket));
    };
    const onStorage = (e) => {
      if (e.key === KEY(bucket)) setItems(readAll(bucket));
    };
    window.addEventListener(CHANGE_EVENT, refresh);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener("storage", onStorage);
    };
  }, [bucket]);

  const isBookmarked = useCallback((slug) =>
    items.some((x) => x.slug === slug), [items]);

  const toggle = useCallback((slug, meta = {}) => {
    const current = readAll(bucket);
    const exists = current.some((x) => x.slug === slug);
    const next = exists
      ? current.filter((x) => x.slug !== slug)
      : [...current, { slug, added_at: Date.now(), ...meta }];
    writeAll(bucket, next);
  }, [bucket]);

  const remove = useCallback((slug) => {
    const current = readAll(bucket);
    writeAll(bucket, current.filter((x) => x.slug !== slug));
  }, [bucket]);

  const clear = useCallback(() => writeAll(bucket, []), [bucket]);

  return { items, isBookmarked, toggle, remove, clear, count: items.length };
}
